// Study Hall page (DESIGN.md 16): boot, page states, the socket, and the glue between the store,
// the view, the composer, the dialogs and the moderator drawer. Runs in its own document at /hall
// (an internal Re:Vault tab or a Re:Vault OS window), so none of it reaches the start page.
//
// Boot: /account/config, then a session, then the socket. The token only ever travels in the
// socket's first frame (`hello`). Everything the server says arrives as an event, goes through the
// store's reducers, and the view re-renders the part that changed. The server is the authority on
// every rule; the client only mirrors enough of them (read-only states, the edit window, counters)
// to explain things before a round trip.
import { getSession, signIn, accountConfig, refreshProfile, keepSessionFresh, renewDue } from "../app/account.js";
import { openableUrl } from "../shared/open-url.js";
import { metric } from "../shared/metric.js";
import * as C from "./copy.js";
import { errText } from "./copy.js";
import { createConn, PROTO } from "./conn.js";
import { createStore, newCid, CAP, CAP_LITE, TYPING_MS } from "./store.js";
import { createView, h, fmtUntil, trustLine } from "./view.js";
import { createComposer } from "./composer.js";
import { gateDialog, handleDialog, reportDialog, confirmDialog, infoDialog, modal, rulesList, pollDialog, nameCard } from "./dialogs.js";
import { createApi, hasModKey } from "./api.js";
import { createMod, queueItems } from "./mod.js";

const PREFIX = __RVM.hall || "";
const LS_SESSION = "revault:account";
const LS_U13 = "revault:hall:u13";
const LS_DENSITY = "revault:hall:density";
const SS_ROOM = "revault:hall:room";
const LS_HOW = "revault:hall:how";
const LS_SOUND = "revault:hall:sound";
// The soundscapes the shell can play (MUSIC_PRESETS in shared/settings-schema.js).
const SOUNDS = ["brown", "white", "rain", "ocean", "fire", "focus"];
const SS_RELOADED = "revault:hall:reloaded";
const ROOM_RE = /^[a-z]{2,24}$/;
const HIDDEN_CLOSE_MS = 10 * 60 * 1000;
// Focusing with the room keeps the socket while you work in another tab, up to 2 hours.
const HIDDEN_FOCUS_MS = 2 * 60 * 60 * 1000;
// A hidden tab's timers can be throttled to once a minute, so its silence check is relaxed too.
const HIDDEN_SILENT_MS = 5 * 60 * 1000;
const RECONNECT_BANNER_MS = 2000;
const BACK_ONLINE_MS = 3000;
const READ_DELAY_MS = 1200;
const TYPING_EVERY_MS = 3000;
const TICK_MS = 15000;

const root = document.getElementById("rv-hall");
const framed = window.parent !== window;
const lite = () => document.documentElement.dataset.perf === "on";
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
function lsGet(k) { try { return localStorage.getItem(k) || ""; } catch (e) { void e; return ""; } }
function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) { void e; } }
function ssGet(k) { try { return sessionStorage.getItem(k) || ""; } catch (e) { void e; return ""; } }
function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { void e; } }
function ssDel(k) { try { sessionStorage.removeItem(k); } catch (e) { void e; } }

// ---------------------------------------------------------------------------------------------
root.dataset.density = lsGet(LS_DENSITY) === "compact" ? "compact" : "cozy";
// /obf-page.js applies the look (font included). Study Hall hosts the look fonts itself (hall.css,
// <html data-fonts="self">) and loads any other from /fonts/css2, so it never asks a third party.
function lookSettings() { try { return JSON.parse(localStorage.getItem("revault:settings") || "{}") || {}; } catch (e) { void e; return {}; } }

// The same word list and check the server runs (hall/words.js), so a refused message never leaves
// the browser. It loads when the page is idle; until then the server's answer is the same.
const WORD_COOL_MS = 5000;
let wordCheck = null, wordSexual = null, wordLoad = null;
function loadWords() {
	if (!wordLoad) wordLoad = import(__RVM.hallWordsJs).then((m) => { wordCheck = m.filter; wordSexual = m.isSexual; }).catch(() => { wordLoad = null; });
	return wordLoad;
}
const hasBlockedWord = (text) => { if (!wordCheck) { loadWords(); return false; } return !!wordCheck.check(text); };
// A message, edit or poll refused here. One with a sexual word is also sent to the server, which
// checks it again and keeps it for moderators for 30 days (the same as when the server refuses
// it). Profanity and slurs are never sent anywhere.
function refusedHere(text, room) {
	if (!hasBlockedWord(text)) return false;
	if (wordSexual && wordSexual(text)) api.authed("me/word-attempt", { method: "POST", body: { cat: "sexual", b: String(text).slice(0, 2000), room: room || "" } }).catch(() => {});
	return true;
}
const store = createStore({ cap: lite() ? CAP_LITE : CAP });
const S = store.S;
const api = createApi(PREFIX);
let phase = "boot"; // boot | card | gate | main | blocked
let everReady = false, connBanner = "", discT = 0, backT = 0, readT = 0, typingT = 0, loadingMore = false;
let gateUi = null, mod = null, hiddenClosed = false, hideT = 0, modqPushed = false, modqPrimed = false;
// The focus lease to re-send after a reconnect ({ room, cyc }), and whether this tab told the
// server it is away (hidden while focusing).
let keepLease = null, away = false;
const lastTyping = new Map();
let helloTok = ""; // the token the current socket said hello with
// When sending opens again, per room (slow mode, the room cap) and for the account (its send rate).
const holdUntil = new Map();
let rateUntil = 0;

const composer = createComposer({ store, onSend, onEdit, onTyping, onUpEdit });
const view = createView(root, {
	store, act, join, more, jumpLatest, toggleMod, openMe, openRules, toggleDensity, lite, timerAct, newPoll, invite,
	openMenu, openTool, setSound, soundOn, howSeen, howDismiss, qotdHidden, qotdHide,
	composer: composer.el,
	ownerKey: hasModKey,
	onScroll: (bottom) => { if (bottom) maybeRead(); },
});
const conn = createConn({
	url: (location.protocol === "https:" ? "wss://" : "ws://") + location.host + PREFIX + "s",
	getToken: () => { const s = getSession(); helloTok = s ? s.token : ""; return helloTok; },
	getHello,
	onEvent,
	onState,
	beacon: onBeacon,
});

// --- boot ------------------------------------------------------------------------------------
async function boot() {
	view.loading();
	if (!PREFIX) { cardOnly(C.ACCOUNTS_OFF); return; } // a server built before Study Hall existed
	const cfg = await accountConfig();
	if (!cfg || !cfg.enabled) { cardOnly(C.ACCOUNTS_OFF); return; }
	const code = takeInvite();
	if (!getSession()) { signedOut(code ? await invitedText(code) : ""); return; }
	if (code) await useInvite(code);
	phase = "boot";
	conn.start();
	// Study Hall can be the only Re:Vault page open, so it renews the session too.
	if (renewDue()) refreshProfile();
	keepSessionFresh();
}

// --- room invites ------------------------------------------------------------------------------
// The code arrives in this tab's sessionStorage (the app took it from /#invite=) or in the address
// (/hall#invite=), and waits there until it has been used, so signing in first doesn't lose it.
// Everything else about invites is a small lazy chunk (hall/invite.js).
const SS_INVITE = "revault:hall:invite";
let inviteNote = "", inv = null;
const invMod = () => inv || (inv = import(__RVM.hallInviteJs).then((m) => m.createInvites({ api, metric, ssGet, ssSet, ssDel, SS_INVITE, SS_ROOM })).catch((e) => { inv = null; throw e; }));
function takeInvite() {
	const m = /^#invite=([A-Za-z2-9]{10})$/.exec(location.hash);
	if (m) { ssSet(SS_INVITE, m[1].toUpperCase()); try { history.replaceState(null, "", location.pathname); } catch (e) { void e; } }
	return ssGet(SS_INVITE);
}
const invitedText = (code) => invMod().then((m) => m.invitedText(code), () => "");
const useInvite = (code) => invMod().then((m) => m.use(code), () => "").then((note) => { inviteNote = note || ""; });
function invite(btn) {
	const r = store.room(S.focus);
	if (r) invMod().then((m) => m.make(r, btn), () => view.toast(C.CLOSED)).then((msg) => { if (msg) view.toast(msg); });
}

function cardOnly(text, extra) {
	phase = "card";
	closeGateUi();
	root.removeAttribute("aria-busy");
	return view.card({ text, ...extra });
}
function signedOut(text) {
	cardOnly(text || C.SIGNED_OUT, {
		button: C.SIGN_IN,
		onClick: async () => {
			try { await signIn(); } catch (e) { void e; cardOnly(C.SIGN_IN_FAILED); }
		},
	});
}
function restart() {
	phase = "boot";
	view.loading();
	conn.start();
}

// The room to ask for and, when its rows are still in memory, the useq to resume from.
function getHello() {
	const out = {};
	const room = S.focus || ssGet(SS_ROOM);
	if (ROOM_RE.test(room)) {
		out.room = room;
		const since = store.sinceFor(room);
		if (since !== undefined) out.since = since;
	}
	return out;
}

// --- socket state ------------------------------------------------------------------------------
function onState(state, info) {
	if (state === "connecting" || state === "retrying") {
		if (everReady && !discT && connBanner !== "reconnecting") discT = setTimeout(() => { discT = 0; setBanner("reconnecting"); }, RECONNECT_BANNER_MS);
		for (const p of S.pending) if (p.state === "sending" && !conn.isOpen()) p.state = "queued";
		if (phase === "main") view.pending();
		return;
	}
	if (state === "closed") closedFor(info.code, info.bye);
}

// The socket was closed on purpose by the server and must not retry on its own.
function closedFor(code, bye) {
	clearTimeout(discT); discT = 0;
	const b = bye && typeof bye === "object" ? bye : {};
	if (mod) mod.close();
	// The socket closes when the token it said hello with expires. If the session was renewed
	// since (here or in another tab), reconnect with the new token instead of signing out.
	if (code === 4001 && renewedSince()) restart();
	else if (code === 4001) signedOut(C.SIGNED_EXPIRED);
	else if (code === 4003) cardOnly(C.BANNED(typeof b.reason === "string" ? b.reason : ""));
	else if (code === 4009) cardOnly(C.REPLACED, { button: C.USE_HERE, onClick: restart });
	else cardOnly(typeof b.msg === "string" && b.msg ? b.msg : C.CLOSED, { button: C.TRY_AGAIN, onClick: restart });
}

function renewedSince() {
	const s = getSession();
	if (!s || !s.token || s.token === helloTok) return false;
	try {
		const exp = Number(JSON.parse(atob(s.token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).exp);
		return Number.isFinite(exp) && exp * 1000 > Date.now();
	} catch (e) { void e; return false; }
}

// After three failed first connects the browser can't tell why (a socket never sees the HTTP
// status of a refused upgrade), so ask over plain HTTP. The beacon answering 503 (off, breaker,
// shedding) or 404 (Study Hall isn't running), or /status.json not listing a working Study Hall,
// means it's closed: show that and stop. Otherwise sockets don't get through this network: say
// so, and keep trying in the background.
async function onBeacon(kind) {
	const status = await api.beacon(kind);
	if (kind !== "ws_blocked" || everReady || phase === "main" || phase === "gate") return;
	const closed = status === 503 || status === 404 || !(await hallListed());
	if (everReady || phase === "main" || phase === "gate") return; // connected meanwhile
	if (closed) { conn.stop(); cardOnly(C.CLOSED, { button: C.TRY_AGAIN, onClick: restart }); return; }
	connBanner = "blocked";
	phase = "blocked";
	root.removeAttribute("aria-busy");
	view.card({ text: C.WS_BLOCKED, button: C.TRY_AGAIN, busy: true, onClick: () => conn.kick() });
}
// /status.json lists Study Hall only while it runs (and says "down" when it failed to start).
// A failed fetch proves nothing either way, so it counts as listed.
async function hallListed() {
	try {
		const j = await (await fetch("/status.json", { cache: "no-store", credentials: "omit" })).json();
		const c = (j && Array.isArray(j.components) ? j.components : []).find((x) => x && x.id === "hall");
		return !!c && c.status !== "down";
	} catch (e) { void e; return true; }
}

function setBanner(kind) {
	connBanner = kind;
	if (phase === "main") view.banners(connBanner);
}

// --- events ------------------------------------------------------------------------------------
function onEvent(t, d, r) {
	switch (t) {
		case "ready": onReady(d); break;
		case "hist": onHist(d); break;
		case "msg": onMsg(d); break;
		case "upd": onUpd(d); break;
		case "typing":
			store.typing(d);
			if (d.room === S.focus && phase === "main") { view.typing(); clearTimeout(typingT); typingT = setTimeout(() => view.typing(), TYPING_MS + 100); }
			break;
		case "heads": store.heads(d); if (phase === "main") view.rail(); break;
		case "tmr": onTmr(d); break;
		case "here": store.here(d); if (phase === "main" && d.room === S.focus) view.header(); break;
		case "room": store.roomEv(d); if (phase === "main") { view.rail(); view.header(); refreshPosting(); } if (mod) mod.roomChanged(); break;
		case "lockdown": store.lockdown(d); refreshPosting(); if (mod) mod.roomChanged(); break;
		case "mode": if (typeof d.mode === "string") S.mode = d.mode; refreshPosting(); if (mod) mod.roomChanged(); break;
		case "you": onYou(d); break;
		case "read": store.read(d); if (phase === "main") view.rail(); break;
		case "notice": onNotice(d); break;
		case "modq": onModq(d); break;
		// Not in protocol v1's event list: tolerated so a block made in another tab can follow
		// here when the server pushes the account's list (the same shape as ready.blocks).
		case "blocks": if (Array.isArray(d.blocks)) { S.blocks = new Set(d.blocks.filter((x) => typeof x === "string")); if (phase === "main") view.log(); } break;
		case "err": if (!r && phase === "main") view.toast(errText(d)); break;
		default: break; // ack for a settled ref, bye (the close follows), unknown events
	}
}

function onReady(d) {
	if (num(d.minClient) > PROTO) {
		// This page is older than the server allows. Reload once; if that didn't help, ask.
		if (!ssGet(SS_RELOADED)) { ssSet(SS_RELOADED, "1"); location.reload(); return; }
		conn.stop();
		cardOnly(C.UPDATED, { button: C.RELOAD, onClick: () => location.reload() });
		return;
	}
	ssDel(SS_RELOADED);
	store.ready(d);
	if (S.gate) { showGate(S.gate, typeof d.gateMsg === "string" ? d.gateMsg : ""); return; }
	closeGateUi();
	clearTimeout(discT); discT = 0;
	const wasMain = phase === "main";
	phase = "main";
	everReady = true;
	// The start page shows Study Hall's focus line during the beta only to browsers that use it.
	lsSet("revault:hall:used", "1");
	root.removeAttribute("aria-busy");
	if (!view.isMounted()) view.mount();
	if (S.focus) ssSet(SS_ROOM, S.focus);
	if (connBanner === "reconnecting" || connBanner === "blocked") {
		setBanner("back");
		clearTimeout(backT);
		backT = setTimeout(() => { if (connBanner === "back") setBanner(""); }, BACK_ONLINE_MS);
	}
	if (store.staff() || hasModKey()) ensureMod();
	if (store.staff() && !modqPushed && !modqPrimed) { modqPrimed = true; primeModq(); }
	if (S.lease) keepLease = S.lease;
	else if (keepLease) resumeLease();
	if (away && !isVisible()) goAway();
	else away = false;
	syncSound();
	renderAll(!wasMain);
	if (inviteNote) { view.toast(inviteNote); inviteNote = ""; }

	// The outbox: anything that never got an answer goes again with the same cid (idempotent).
	for (const p of S.pending.slice()) if (p.state === "queued") trySend(p);
}

function renderAll(fresh) {
	view.rail();
	view.header();
	refreshPosting();
	const r = store.room(S.focus);
	composer.setRoom(r ? r.name : "");
	composer.setCheckIn(store.checkIn(S.focus));
	holdFor(S.focus);
	view.log();
	if (fresh) composer.focus();
}

// The send button counts down instead of letting a message bounce: after your own message in a
// slow-mode room, and after the server said to wait (slow mode and the room cap per room, the
// send rate for every room). Staff skip slow mode, so nothing is held for them after a send.
function hold(room, ms) {
	const until = Date.now() + Math.max(0, ms);
	if (room) { if (until > (holdUntil.get(room) || 0)) holdUntil.set(room, until); }
	else if (until > rateUntil) rateUntil = until;
	holdFor(S.focus);
}
function holdFor(room) {
	const left = Math.max(rateUntil, holdUntil.get(room) || 0) - Date.now();
	if (left > 0) composer.wait(left); else composer.clearWait();
}

function onHist(d) {
	store.hist(d);
	if (phase !== "main" || d.room !== S.focus) return;
	view.log();
	maybeRead();
}

function onMsg(d) {
	if (!store.msg(d)) return;
	if (phase !== "main") return;
	if (d.room === S.focus) { view.append(store.findRow(d.id)); maybeRead(); }
	else view.rail();
}

function onUpd(d) {
	const hadQotd = !!(store.room(d.room) || {}).qotd;
	const { row, needBody } = store.upd(d);
	if (hadQotd && !(store.room(d.room) || {}).qotd && phase === "main" && d.room === S.focus) view.header();
	if (!row || phase !== "main" || d.room !== S.focus) return;
	// A state change can blank reply previews elsewhere in the log, so repaint it all then; an edit
	// only changes this row and the previews of its replies.
	if ("st" in d) view.log();
	else { view.update(row); if (typeof d.b === "string") for (const r of store.repliesTo(d.room, d.id)) view.update(r); }
	// A restored message whose text wasn't included: fetch the rows changed from its useq on.
	if (needBody && num(d.useq) > 0) conn.request("join", { room: d.room, since: num(d.useq) - 1 }).catch(() => {});
}

// A care card answers the message that prompted it, and the server sends it just before that
// message's ack. While one of your sends is still in flight, hold the card until the ack lands (at
// most 3 s), so it shows under your message instead of above it.
let noticeWait = [];
function onNotice(d) {
	if (phase !== "main") return;
	if (!store.pendingIn(S.focus).length) { view.notice(d); return; }
	noticeWait.push(d);
	setTimeout(flushNotices, 3000);
}
function flushNotices() {
	const q = noticeWait;
	noticeWait = [];
	if (phase === "main") for (const d of q) view.notice(d);
}

function onYou(d) {
	const wasStaff = store.staff();
	store.you(d);
	if (store.staff() && !wasStaff) ensureMod();
	if (phase !== "main") return;
	refreshPosting();
	composer.refresh();
	if (store.staff() !== wasStaff) { view.header(); view.log(); }
}

// A moderator arriving should see at once whether anything is waiting (at launch the owner
// moderates alone, backed by automod): count the open queue until a `modq` push says so.
async function primeModq() {
	let j;
	try { j = await api.authed("mod/queue?status=open"); } catch (e) { void e; return; }
	if (modqPushed) return;
	const items = queueItems(j);
	onModq({ open: items.length, urgent: items.filter((it) => it.urgent).length, reports: items.length });
}

function onModq(d) {
	const prev = num(S.modq && S.modq.urgent);
	modqPushed = true;
	// open/urgent drive the shield badge; reports, support and alerts say which drawer tab holds them.
	S.modq = { open: Math.max(0, num(d.open)), urgent: Math.max(0, num(d.urgent)), reports: Math.max(0, num(d.reports)), support: Math.max(0, num(d.support)), alerts: Math.max(0, num(d.alerts)) };
	if (phase !== "main") return;
	view.header();
	if (mod) mod.poke();
	if (S.modq.urgent > prev) view.toast(C.URGENT_TOAST);
}

// Read-only states: the banner explains, the composer is disabled with the same words.
function refreshPosting() {
	if (phase !== "main") return;
	view.banners(connBanner);
	const why = store.blockReason(S.focus);
	const s = store.sanctionActive();
	const text = {
		readonly: C.READONLY,
		timeout: s ? C.TIMED_OUT(fmtUntil(s.until, store.serverNow()), s.reason || "") : "",
		announce: C.ANNOUNCE_ONLY,
		locked: C.LOCKED,
		lockdown: C.LOCKDOWN_NEW,
		lockdown3: C.LOCKDOWN_ALL,
		retired: C.RETIRED,
	}[why] || "";
	composer.setBlocked(text);
	composer.setCheckIn(!why && store.checkIn(S.focus));
}

// --- gates (5.4, 16.2) ------------------------------------------------------------------------
function showGate(kind, msg) {
	phase = "gate";
	root.removeAttribute("aria-busy");
	if (mod) mod.close();
	// Under 13 is permanent for that account, and this browser remembers it for any account.
	if (kind === "age_blocked" || (kind === "rules" && lsGet(LS_U13))) {
		lsSet(LS_U13, "1");
		conn.stop();
		cardOnly(C.UNDER_13);
		return;
	}
	const group = kind === "rules" ? "rules" : "handle";
	const reason = msg || (kind === "handle_taken" ? C.HANDLE_TAKEN : "");
	if (gateUi && gateUi.group === group && gateUi.api.dlg.open) {
		gateUi.api.busy(false);
		if (reason) gateUi.api.error(reason);
		return;
	}
	closeGateUi();
	view.card({ text: C.GATE_NOTE });
	const api2 = group === "rules" ? gateDialog(submitGate) : handleDialog({ current: (S.me && S.me.h) || "", reason, onSubmit: submitHandle });
	gateUi = { group, api: api2 };
}
function closeGateUi() {
	if (!gateUi) return;
	const g = gateUi;
	gateUi = null;
	g.api.close();
}
function submitGate(payload, a) {
	conn.request("gate", payload).catch((e) => { a.error(errText(e)); a.busy(false); });
}
// Used by the gate and by "Change name": the server answers with `ready` (the new name) and an ack.
function submitHandle(value, a) {
	if (hasBlockedWord(value)) { a.error(C.NAME_WORD); a.busy(false); return; }
	conn.request("handle", { h: value })
		.then(() => { if (phase === "main") a.close(); })
		.catch((e) => { a.error(errText(e)); a.busy(false); });
}

// --- sending (14: optimistic rows, outbox, replay) --------------------------------------------
// A refused word: the notice, and the composer waits (the server holds the same wait).
function wordStop(ms, msg) {
	const wait = num(ms) > 0 ? num(ms) : WORD_COOL_MS;
	composer.cooldown(wait, msg);
	hold("", wait);
}

function onSend(text, reply) {
	const room = S.focus;
	if (!store.room(room)) return;
	if (refusedHere(text, room)) { wordStop(WORD_COOL_MS); return; }
	const p = { cid: newCid(), room, b: text, rp: reply ? reply.id : 0, rpH: reply ? reply.h : "", state: conn.isOpen() ? "sending" : "queued" };
	if (!store.addPending(p)) { composer.error(C.OUTBOX_FULL); return; }
	composer.clear();
	view.pending();
	view.scrollBottom(false);
	trySend(p, 0);
}

function trySend(p, tries) {
	if (!conn.isOpen()) { p.state = "queued"; if (phase === "main") view.pending(); return; }
	p.state = "sending";
	if (phase === "main") view.pending();
	const d = { room: p.room, cid: p.cid, b: p.b };
	if (p.rp) d.rp = p.rp;
	conn.request("send", d).then((res) => {
		if (!store.removePending(p.cid)) return; // already settled by an earlier answer
		const m = res && res.m;
		const added = m && store.msg(m);
		const r = store.room(p.room);
		// A check-in isn't slowed (the block allows one anyway); a break message is.
		if (r && r.slow > 0 && !store.staff() && !store.checkIn(p.room)) hold(p.room, r.slow * 1000);
		if (phase !== "main") return;
		view.pending();
		if (added && m.room === S.focus) { view.append(store.findRow(m.id)); view.scrollBottom(false); }
		if (noticeWait.length) flushNotices();
		maybeRead();
	}).catch((e) => {
		if (noticeWait.length) flushNotices();
		if (!S.pending.includes(p)) return;
		const code = e && e.code;
		if (code === "disconnected") { p.state = "queued"; if (phase === "main") view.pending(); return; }
		if (code === "timeout") {
			if ((tries || 0) < 2 && conn.isOpen()) trySend(p, (tries || 0) + 1);
			else { p.state = "queued"; if (phase === "main") view.pending(); }
			return;
		}
		store.removePending(p.cid);
		sendFailed(p, e || {});
	});
}

// Automod, limits and state errors: the row goes, the text comes back, the reason shows under it.
function sendFailed(p, e) {
	if (phase === "main") view.pending();
	if (p.room === S.focus) composer.restore(p.b, p.rp ? { id: p.rp, h: p.rpH } : null);
	composer.error(errText(e));
	const code = e.code;
	if ((code === "automod" || code === "cooldown") && num(e.retryMs) > 0) wordStop(e.retryMs, errText(e));
	if ((code === "slowmode" || code === "busy") && num(e.retryMs) > 0) hold(p.room, num(e.retryMs));
	if (code === "rate_limited" && num(e.retryMs) > 0) hold("", num(e.retryMs));
	if (code === "timed_out" && S.me) S.me.sanction = { kind: "timeout", until: num(e.until), reason: typeof e.reason === "string" ? e.reason : "" };
	if (code === "lockdown") S.lockdownHit = true;
	if (code === "closed") S.mode = "readonly";
	if (code === "locked" || code === "read_only") { const r = store.room(p.room); if (r) r.canPost = false; }
	if (code === "timed_out" || code === "lockdown" || code === "closed" || code === "locked" || code === "read_only") refreshPosting();
}

function onEdit(m, text) {
	if (refusedHere(text, m.room)) { wordStop(WORD_COOL_MS); return; }
	composer.setBusy(true);
	conn.request("edit", { id: m.id, b: text })
		.then((d) => {
			composer.setBusy(false);
			if (d && d.m && store.msg(d.m)) { const row = store.findRow(m.id); if (row) view.update(row); for (const r of store.repliesTo(m.room, m.id)) view.update(r); }
			composer.clear();
		})
		.catch((e) => {
			composer.setBusy(false);
			composer.error(errText(e));
			if (e && (e.code === "automod" || e.code === "cooldown") && num(e.retryMs) > 0) wordStop(e.retryMs, errText(e));
		});
}

function onTyping() {
	if (lite() || phase !== "main" || store.blockReason(S.focus)) return;
	const t = Date.now();
	if (t - (lastTyping.get(S.focus) || 0) < TYPING_EVERY_MS) return;
	lastTyping.set(S.focus, t);
	conn.fire("typing", { room: S.focus });
}

function onUpEdit() {
	const m = store.lastEditable(S.focus);
	if (!m) return false;
	composer.setEdit(m);
	return true;
}

// --- rooms, history, read cursor ---------------------------------------------------------------
function join(id) {
	if (!store.room(id)) return;
	if (id === S.focus) { view.rail(); return; }
	S.focus = id;
	ssSet(SS_ROOM, id);
	metric("hall:" + id);
	view.resetMark();
	view.closePicker();
	composer.cancel();
	renderAll(false);
	const since = store.sinceFor(id);
	conn.request("join", since !== undefined ? { room: id, since } : { room: id }).catch((e) => {
		if (e && e.code !== "disconnected" && e.code !== "timeout") view.toast(errText(e));
	});
}

function more() {
	const b = S.bufs.get(S.focus);
	if (loadingMore || !b || !b.more || !b.ids.length || !conn.isOpen()) return;
	loadingMore = true;
	conn.request("more", { room: S.focus, before: b.ids[0] }).catch(() => {}).finally(() => { loadingMore = false; });
}

// Older pages pushed the newest rows out of memory: start again from the latest.
function jumpLatest() {
	conn.request("join", { room: S.focus }).catch(() => {});
}

function isVisible() { return !document.hidden && frameVisible; }

function maybeRead() {
	if (phase !== "main" || !isVisible() || !view.atBottom()) return;
	const r = store.room(S.focus);
	const list = store.list(S.focus);
	if (!r || !list.length) return;
	const last = list[list.length - 1].id;
	if (last <= r.read) return;
	clearTimeout(readT);
	readT = setTimeout(() => {
		readT = 0;
		const r2 = store.room(S.focus);
		if (!r2 || r2.id !== r.id || last <= r2.read) return;
		r2.read = last;
		if (r2.read >= r2.head) r2.ment = 0;
		conn.fire("read", { room: r2.id, id: last });
		view.rail();
	}, READ_DELAY_MS);
}

// --- message actions ---------------------------------------------------------------------------
function act(verb, m0, e) {
	const m = store.findRow(m0.id) || m0;
	switch (verb) {
		case "reply": composer.setReply(m); break;
		case "toggle": react(m, e); break;
		case "edit": if (store.canEdit(m)) composer.setEdit(m); break;
		case "del": del(m); break;
		case "report": report(m); break;
		case "block": block(m, true); break;
		case "unblock": block(m, false); break;
		case "remove": case "restore": ensureMod().act(verb, { msgId: m.id, h: m.a.h }); break;
		case "approve": ensureMod().approve(m.id); break;
		case "timeout": case "ban": ensureMod().act(verb, { u: m.a.u, h: m.a.h }); break;
		case "user": ensureMod().userCard(m.a.u); break;
		case "card": openCard(m); break;
		case "vote": vote(m, e); break;
		default: break;
	}
}

function react(m, e) {
	if (!store.canReact(m.room) || m.st !== 0 || !(e >= 0 && e <= 5)) return;
	const on = !store.mineHas(m.id, e);
	store.toggleMine(m.id, e, on);
	view.update(m);
	conn.request("react", { id: m.id, e, on })
		.then((d) => { store.ackReact({ ...d, id: m.id }); const row = store.findRow(m.id); if (row) view.update(row); })
		.catch((err) => {
			store.toggleMine(m.id, e, !on);
			const row = store.findRow(m.id);
			if (row) view.update(row);
			if (err && err.code !== "already" && err.code !== "disconnected") view.toast(errText(err));
		});
}

async function del(m) {
	if (!(await confirmDialog(C.CONFIRM_DELETE, C.DELETE, true))) return;
	conn.request("del", { id: m.id })
		.then(() => { if (store.upd({ id: m.id, room: m.room, st: m.st === 4 ? 5 : 1 }).row && m.room === S.focus) view.log(); })
		.catch((e) => view.toast(errText(e)));
}

function report(m) {
	reportDialog({
		canBlock: m.a.s === 0 && !S.blocks.has(m.a.u),
		onSubmit: (d, a) => {
			const payload = { id: m.id, cat: d.cat };
			if (d.note) payload.note = d.note.slice(0, 300);
			if (d.block) payload.block = true;
			conn.request("report", payload)
				.then(() => {
					a.close();
					if (d.block) { S.blocks.add(m.a.u); view.log(); }
					if (d.cat === "self_harm") infoDialog(C.REPORT_TITLE, [C.REPORT_THANKS, C.REPORT_SELF_HARM]);
					else view.toast(C.REPORT_THANKS);
				})
				.catch((e) => { a.error(errText(e)); a.busy(false); });
		},
	});
}

async function block(m, on) {
	if (!(await confirmDialog(on ? C.CONFIRM_BLOCK(m.a.h) : C.CONFIRM_UNBLOCK(m.a.h), on ? C.T.block : C.T.unblock, on))) return;
	conn.request("block", { u: m.a.u, on })
		.then((d) => {
			// `blocks` is the account's full list after the change (the same shape as ready.blocks).
			if (d && Array.isArray(d.blocks)) S.blocks = new Set(d.blocks.filter((x) => typeof x === "string"));
			if (on) S.blocks.add(m.a.u); else S.blocks.delete(m.a.u);
			view.log();
		})
		.catch((e) => view.toast(errText(e)));
}

// --- shared focus timer (docs/chat/BETA-2026-10.md section 3) --------------------------------------
function onTmr(d) {
	const had = !!(S.lease && S.lease.room === d.room);
	const res = store.tmr(d);
	if (!res) return;
	syncSound();
	if (had && !S.lease) { keepLease = null; if (!isVisible()) armHiddenClose(); }
	else if (S.lease) keepLease = S.lease;
	if (had && res.changed && S.lease) chime();
	if (phase !== "main") return;
	view.rail();
	if (d.room === S.focus) { view.header(); refreshPosting(); }
	else if (S.focus === "hangout") view.timer();
	if (had && res.changed && isVisible()) view.toast(!S.lease ? C.TIMER_RESET : res.room.tmr.ph === "break" ? C.BREAK_NOW : C.FOCUS_NOW);
}
function setLease(l) {
	store.setLease(l);
	keepLease = S.lease;
	syncSound();
}

// --- room sound (a soundscape while you focus with the room) ------------------------------------
// The shell plays it (app/music.js focusPlay/focusDim/focusStop): local audio only, nothing is
// streamed, and the student's own music always wins. Off until switched on; Lite mode never plays.
// The block's phase is posted even with the sound off (preset "off" plays nothing), so the shell's
// "Studying now" yes/no counts everyone in a focus block.
function soundOn() { return lsGet(LS_SOUND) === "1"; }
function soundPreset() { const p = lookSettings().focusSound; return SOUNDS.includes(p) ? p : "rain"; }
let soundPhase = "", soundKey = "";
function syncSound(force) {
	let want = "";
	if (S.lease) {
		const r = store.room(S.lease.room);
		want = r && r.tmr && r.tmr.ph === "break" ? "break" : r && r.tmr && r.tmr.ph === "focus" ? "work" : "";
	}
	const preset = want && soundOn() && !lite() ? soundPreset() : "off";
	const key = want ? want + " " + preset : "";
	if (key === soundKey && !force) return;
	if (!key && !soundKey) return;
	soundKey = key;
	soundPhase = want;
	if (!framed) return;
	try { window.parent.postMessage({ type: "revault-focus", phase: want || "stop", preset }, location.origin); } catch (e) { void e; }
}
function setSound(on) {
	lsSet(LS_SOUND, on ? "1" : "");
	syncSound();
}
// After a reconnect (or a server restart), ask for the lease back. The server takes it if the
// block is still running, or starts a new one if the room went idle meanwhile and we may start one.
function resumeLease() {
	const l = keepLease;
	conn.request("focus", { room: l.room, on: true, cyc: l.cyc })
		.then((res) => { if (res.tmr) store.tmr({ room: l.room, ...res.tmr }); setLease(res.lease); if (phase === "main") { view.rail(); view.header(); } })
		.catch((e) => { if (e && e.code !== "disconnected" && e.code !== "timeout") { keepLease = null; if (e.tmr) store.tmr({ room: l.room, ...e.tmr }); if (phase === "main") { view.rail(); view.header(); } } });
}
async function timerAct(act) {
	const room = S.focus;
	if (act === "stop" && !(await confirmDialog(C.STAFF_STOP_Q, C.STAFF_STOP, true))) return;
	const req = act === "start" || act === "stop" ? conn.request("timer", { room, act }) : conn.request("focus", { room, on: act === "join" });
	unlockChime();
	req.then((res) => {
		if (res.tmr) store.tmr({ room, ...res.tmr });
		setLease(res.lease);
		if (phase !== "main") return;
		view.rail(); view.header(); refreshPosting();
		if (act === "start" || act === "join") view.toast(C.FOCUS_STARTED);
	}).catch((e) => {
		if (e && e.tmr) store.tmr({ room, ...e.tmr });
		if (phase === "main") { view.header(); view.toast(errText(e)); }
	});
}
// A soft two-note chime at phase changes for people focusing (never in Lite mode). The audio
// context is created on the Focus click, so the browser lets it play later from a hidden tab.
let ac = null;
function unlockChime() {
	if (ac || lite()) return;
	try { ac = new (window.AudioContext || window.webkitAudioContext)(); } catch (e) { void e; ac = null; }
}
function chime() {
	if (!ac || lite()) return;
	try {
		if (ac.state === "suspended") ac.resume();
		const t = ac.currentTime;
		const out = ac.createGain();
		out.gain.value = 0.15;
		out.connect(ac.destination);
		[660, 880].forEach((f, i) => {
			const o = ac.createOscillator(), g = ac.createGain();
			o.type = "sine"; o.frequency.value = f;
			g.gain.setValueAtTime(0, t + i * 0.18);
			g.gain.linearRampToValueAtTime(1, t + i * 0.18 + 0.02);
			g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.18 + 0.5);
			o.connect(g); g.connect(out);
			o.start(t + i * 0.18); o.stop(t + i * 0.18 + 0.55);
		});
	} catch (e) { void e; }
}

// --- polls ---------------------------------------------------------------------------------------
function newPoll() {
	const r = store.room(S.focus);
	if (!r || !r.polls) return;
	const room = r.id;
	pollDialog({
		blockRunning: !!(r.tmr && r.tmr.ph === "focus"),
		onSubmit: (d, a) => {
			if (refusedHere(d.q + "\n" + d.o.join("\n"), room)) { a.error(C.WORD_BLOCKED); a.busy(false); hold("", WORD_COOL_MS); return; }
			conn.request("poll", { room, cid: newCid(), q: d.q, o: d.o, dur: d.dur })
				.then((res) => {
					a.close();
					if (res.m && store.msg(res.m) && res.m.room === S.focus && phase === "main") { view.append(store.findRow(res.m.id)); view.scrollBottom(false); }
				})
				.catch((e) => {
					a.error(errText(e)); a.busy(false);
					if (e && (e.code === "automod" || e.code === "cooldown") && num(e.retryMs) > 0) hold("", num(e.retryMs));
				});
		},
	});
}
function vote(m, opt) {
	if (!m.poll || m.st !== 0) return;
	const next = opt === -1 || store.myVote(m.id) === opt ? -1 : opt;
	conn.request("vote", { id: m.id, opt: next })
		.then((d) => { const row = store.setVote(m.id, Number.isInteger(d.mine) ? d.mine : -1, d); if (row) view.update(row); })
		.catch((e) => view.toast(errText(e)));
}

// --- name card and status (section 4) --------------------------------------------------------------
async function openCard(m) {
	if (!m || m.a.s === 3) return;
	let label = "";
	try {
		const d = await conn.request("pres", { u: m.a.u });
		if (d.p === 0) label = C.PRES[0];
		else if (d.p === 1) { const r = store.room(d.r); label = C.PRES[1] + (r ? r.name : ""); if (!r) label = C.PRES[0]; }
		else if (d.p === 2) { const r = store.room(d.r); label = C.PRES[2] + (r ? " (" + r.name + ")" : ""); }
	} catch (e) { void e; }
	const staffButton = store.staff() && m.a.u !== (S.me && S.me.u) ? { label: C.T.user, run: (a) => { a.close(); ensureMod().userCard(m.a.u); } } : null;
	nameCard({ h: m.a.h, label, staffButton });
}
function statusToggle(me) {
	const box = h("input", { type: "checkbox", class: "rv-hall-toggle" });
	box.checked = me.pres !== false;
	box.addEventListener("change", () => {
		const on = box.checked;
		box.disabled = true;
		conn.request("presvis", { on })
			.then(() => { if (S.me) S.me.pres = on; })
			.catch((e) => { box.checked = !on; view.toast(errText(e)); })
			.finally(() => { box.disabled = false; });
	});
	return h("label", { class: "rv-hall-status-toggle" }, box, h("span", null, h("strong", { text: C.SHOW_STATUS }), h("span", { class: "rv-hall-muted", text: C.SHOW_STATUS_HINT })));
}

// --- menus ------------------------------------------------------------------------------------
function ensureMod() {
	if (!mod) mod = createMod({ api, store, owner: () => !!(S.me && S.me.staff === 2) || hasModKey(), toast: (t) => view.toast(t) });
	return mod;
}
function toggleMod() {
	const el = view.drawer();
	if (el) ensureMod().toggle(el);
}

function openRules() {
	modal({ title: C.RULES_HEADING, body: [rulesList(), trustLine()], actions: [{ label: C.OK, primary: true, run: (a) => a.close() }] });
}

// Under 640 px: what the rail holds on a wider screen.
function openMenu() {
	modal({
		title: C.MENU, cls: "rv-hall-dlg-sm",
		body: [],
		actions: [
			{ label: C.RULES_BUTTON, run: (a) => { a.close(); openRules(); } },
			{ label: C.MENU_ME, run: (a) => { a.close(); openMe(); } },
			{ label: C.DENSITY + ": " + (root.dataset.density === "compact" ? C.COMPACT : C.COZY), run: (a) => { a.close(); toggleDensity(); } },
			{ label: C.CLOSE, primary: true, run: (a) => a.close() },
		],
	});
}

// "How this room works": dismissed per room, on this device only.
function howState() { try { return JSON.parse(lsGet(LS_HOW) || "{}") || {}; } catch (e) { void e; return {}; } }
function howSeen(room) { return !!howState()[room]; }
function howDismiss(room) {
	const s = howState();
	s[room] = 1;
	lsSet(LS_HOW, JSON.stringify(s));
	composer.focus();
}

// A hidden question of the day stays hidden on this device until a new one is posted.
const LS_QOTD = "revault:hall:qotd-hidden";
function qotdList() { try { const a = JSON.parse(lsGet(LS_QOTD) || "[]"); return Array.isArray(a) ? a : []; } catch (e) { void e; return []; } }
function qotdHidden(id) { return !!id && qotdList().includes(Number(id)); }
function qotdHide(id) { lsSet(LS_QOTD, JSON.stringify([Number(id), ...qotdList()].slice(0, 8))); composer.focus(); }

// A pinned Re:Vault tool opens in a Re:Vault tab (or a new window when Study Hall runs on its own).
function openTool(id) {
	const url = "/tools/" + id;
	if (framed) { try { window.parent.postMessage({ type: "revault-open", url }, location.origin); } catch (e) { void e; } return; }
	window.open(url, "_blank", "noopener");
}

function openMe() {
	const me = S.me || {};
	const next = Number(me.handleNext) || 0;
	const nextLine = next > store.serverNow() ? h("p", { class: "rv-hall-muted", text: C.NAME_NEXT(new Date(next).toLocaleDateString([], { month: "short", day: "numeric" })) }) : null;
	modal({
		title: me.h || C.NAME,
		body: [h("p", { class: "rv-hall-muted", text: C.ME_HINT }), nextLine, statusToggle(me)],
		actions: [
			{ label: C.DELETE_DATA, danger: true, run: (a) => { a.close(); deleteData(); } },
			{ label: C.DOWNLOAD_DATA, run: (a) => downloadData(a) },
			{ label: C.RULES_BUTTON, run: (a) => { a.close(); openRules(); } },
			{ label: C.CHANGE_NAME, run: (a) => { a.close(); handleDialog({ current: me.h || "", dismiss: true, onSubmit: submitHandle }); } },
			{ label: C.CLOSE, primary: true, run: (a) => a.close() },
		],
	});
}

// Download my Study Hall data: one JSON file, made on request and never stored.
async function downloadData(a) {
	a.busy(true);
	try {
		const blob = await api.download("me/export");
		const url = URL.createObjectURL(blob);
		const link = h("a", { href: url, download: "study-hall-data.json", class: "rv-hall-offscreen" });
		document.body.append(link);
		link.click();
		link.remove();
		setTimeout(() => URL.revokeObjectURL(url), 60000);
		a.close();
		view.toast(C.DOWNLOAD_STARTED);
	} catch (e) {
		a.busy(false);
		a.error(e && e.message ? e.message : C.ERR.internal);
	}
}

async function deleteData() {
	if (!(await confirmDialog(C.DELETE_DATA, C.DELETE, true, C.DELETE_DATA_Q))) return;
	try {
		await api.authed("me/delete", { method: "POST", body: {} });
	} catch (e) {
		view.toast(e && e.message ? e.message : C.ERR.internal);
		return;
	}
	// The account starts over: a fresh connection shows the rules card again.
	S.pending.length = 0;
	S.bufs.clear();
	S.focus = "";
	ssDel(SS_ROOM);
	conn.stop();
	everReady = false;
	infoDialog(C.NAME, [C.DELETE_DATA_DONE]);
	restart();
}

function toggleDensity() {
	root.dataset.density = root.dataset.density === "compact" ? "cozy" : "compact";
	lsSet(LS_DENSITY, root.dataset.density);
	view.rail();
}

// --- links (16.5): never a raw _blank window, always a proxied Re:Vault tab --------------------
async function copyText(text) {
	try { await navigator.clipboard.writeText(text); return true; } catch (e) { void e; }
	try {
		const ta = h("textarea", { class: "rv-hall-offscreen", readonly: "" });
		ta.value = text;
		document.body.append(ta);
		ta.select();
		const ok = document.execCommand("copy");
		ta.remove();
		return ok;
	} catch (e) { void e; return false; }
}
function onLinkClick(e) {
	const a = e.target && e.target.closest ? e.target.closest("a[data-ext]") : null;
	if (!a) return;
	e.preventDefault();
	if (e.type === "auxclick") return; // a middle click would open the raw URL outside Re:Vault
	const url = openableUrl(a.href);
	if (!url) return;
	if (framed) {
		try { window.parent.postMessage({ type: "revault-open-url", url }, location.origin); } catch (err) { void err; }
		return;
	}
	copyText(url).then((ok) => view.toast(ok ? C.LINK_COPIED : url));
}
document.addEventListener("click", onLinkClick);
document.addEventListener("auxclick", onLinkClick);

// --- visibility, network, session ---------------------------------------------------------------
let frameVisible = true;
try { frameVisible = !(window.frameElement && window.frameElement.hidden); } catch (e) { void e; }
window.addEventListener("message", (e) => {
	if (e.origin !== location.origin || e.source !== window.parent || !e.data || e.data.type !== "revault-frame-visible") return;
	frameVisible = !!e.data.visible;
	visibilityChanged();
});
document.addEventListener("visibilitychange", visibilityChanged);

// Hidden for 10 minutes (the browser tab, or our Re:Vault tab / OS window): close the socket.
// Becoming visible again reconnects and resumes with a delta.
function visibilityChanged() {
	if (!isVisible()) {
		armHiddenClose();
		if (S.lease && phase === "main") goAway();
		return;
	}
	clearTimeout(hideT); hideT = 0;
	conn.setSilentMs(0);
	if (hiddenClosed) { hiddenClosed = false; conn.start(); }
	else { conn.kick(); comeBack(); }
	maybeRead();
}
// Close a hidden tab's socket after 10 minutes, or after 2 hours while it holds a focus lease.
function armHiddenClose() {
	clearTimeout(hideT);
	hideT = setTimeout(() => {
		hideT = 0;
		const st = conn.state();
		if (!isVisible() && phase === "main" && st !== "stopped" && st !== "closed") { hiddenClosed = true; conn.stop(1000); }
	}, S.lease ? HIDDEN_FOCUS_MS : HIDDEN_CLOSE_MS);
}
// Hidden while focusing: tell the server to stop sending room traffic (timer and heads still come).
function goAway() {
	if (!conn.isOpen()) { away = true; return; }
	away = true;
	conn.setSilentMs(HIDDEN_SILENT_MS);
	conn.request("join", { room: "" }).catch(() => {});
}
// Visible again: back into the room, with only what changed meanwhile.
function comeBack() {
	if (!away) return;
	away = false;
	if (!conn.isOpen() || !S.focus) return;
	const since = store.sinceFor(S.focus);
	conn.request("join", since !== undefined ? { room: S.focus, since } : { room: S.focus }).catch(() => {});
}
window.addEventListener("online", () => conn.kick());
window.addEventListener("pagehide", () => {
	if (soundPhase && framed) { try { window.parent.postMessage({ type: "revault-focus", phase: "stop" }, location.origin); } catch (e) { void e; } }
	soundPhase = soundKey = "";
	conn.stop(1000);
});

// Back/forward cache: the page comes back as it was, minus its socket.
window.addEventListener("pageshow", (e) => { if (e.persisted && (phase === "main" || phase === "boot")) conn.start(); });

// Sign-in finishing in another tab (or signing out there) changes what this page can do.
window.addEventListener("storage", (e) => {
	if (e.key !== LS_SESSION) return;
	const has = !!getSession();
	if (has && phase === "card" && !everReady && conn.state() !== "open") restart();
	else if (!has && phase !== "card") { conn.stop(); signedOut(); }
});

// Timeouts and lockdowns end on their own; the typing line expires. Cheap, so just re-check.
setInterval(() => { if (phase === "main") { refreshPosting(); view.typing(); } }, TICK_MS);
// The timer's countdown text (only the strip's text changes).
setInterval(() => { if (phase === "main" && isVisible()) view.timer(); }, 1000);

boot();
// Load the word check while the page is idle, so the first send already has it.
(window.requestIdleCallback || ((fn) => setTimeout(fn, 1500)))(() => loadWords());
